import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Plus, ClipboardList, Search, Loader, RefreshCw, BookOpen, Calendar, Globe, Layers, BarChart2 } from "lucide-react";
import { lmsGetExams, lmsDeleteExam, lmsGetTeacherByUserId, lmsGetTeacherClassSubjects, lmsGetBatches } from "../api/lmsApi";
import { fetchLmsSchoolClasses, fetchLmsClassSubjects } from "../redux/lmsSlice";
import { ExamModal, ExamCard, ExamDetailDrawer, EXAM_TYPES, EXAM_TYPE_LABELS, EXAM_TYPE_COLORS } from "./Exams";
import "../lms.css";

const getTeacherData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

export default function TeacherExams() {
  const dispatch      = useDispatch();
  const nav           = useNavigate();
  const userData      = getTeacherData();
  const adminId       = userData?.admin_id || null;
  const schoolId      = userData?.school_id || userData?.admin_id || null;
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);
  const classSubjects = useSelector((s) => s.lms?.classSubjects || []);

  const [resolvedTeacherId, setResolvedTeacherId] = useState(userData?.teacher_id || null);
  const [teacherClasses,    setTeacherClasses]    = useState(null);
  const [teacherSubjects,   setTeacherSubjects]   = useState(null);
  const [exams,       setExams]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [typeFilter,  setTypeFilter]  = useState("all");
  const [mainTab,     setMainTab]     = useState("all"); // 'all' | 'online' | 'offline'
  const [batches,     setBatches]     = useState([]);
  const [showModal,   setShowModal]   = useState(false);
  const [editExam,    setEditExam]    = useState(null);
  const [viewExam,    setViewExam]    = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  useEffect(() => {
    setBatchFilter("");
    setBatches([]);
    if (!classFilter) return;
    lmsGetBatches({ class_id: classFilter, school_id: schoolId })
      .then((r) => setBatches(r.data?.data || r.data || []))
      .catch(() => {});
  }, [classFilter, schoolId]);

  const loadExams = () => {
    if (!schoolId || !resolvedTeacherId) return;
    setLoading(true);
    const params = { school_id: schoolId, teacher_id: resolvedTeacherId };
    if (classFilter) params.class_id = classFilter;
    lmsGetExams(params)
      .then((res) => setExams(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const setupTeacher = (tid) => {
    setResolvedTeacherId(tid);
    lmsGetTeacherClassSubjects(tid)
      .then((res) => {
        const assignments = res?.data?.data || [];
        const seenClasses = new Set();
        const classes = [];
        assignments.forEach((a) => {
          if (!seenClasses.has(a.class_id)) {
            seenClasses.add(a.class_id);
            classes.push({ class_id: a.class_id, class_name: a.class_name });
          }
        });
        setTeacherClasses(classes);
        setTeacherSubjects(assignments.map((a) => ({
          class_id: a.class_id, subject_id: a.subject_id,
          subject_name: a.subject_name, id: a.subject_id,
        })));
      })
      .catch(() => { setTeacherClasses([]); setTeacherSubjects([]); });
  };

  useEffect(() => {
    dispatch(fetchLmsSchoolClasses(schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));

    const tid = userData?.teacher_id;
    if (tid) { setupTeacher(tid); }
    else if (adminId) {
      lmsGetTeacherByUserId(adminId)
        .then((res) => { if (res?.data?.id) setupTeacher(res.data.id); else { setTeacherClasses([]); setTeacherSubjects([]); } })
        .catch(() => { setTeacherClasses([]); setTeacherSubjects([]); });
    } else {
      setTeacherClasses([]);
      setTeacherSubjects([]);
    }
  }, [adminId]);

  useEffect(() => {
    if (resolvedTeacherId) loadExams();
  }, [resolvedTeacherId, schoolId, classFilter]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await lmsDeleteExam(confirmDel.id);
      toast.success("Exam deleted");
      setConfirmDel(null);
      loadExams();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  // Group exams by mode
  const onlineExams  = exams.filter((e) => e.mode === "online");
  const offlineExams = exams.filter((e) => e.mode !== "online");

  const currentPool = mainTab === "online"
    ? onlineExams
    : mainTab === "offline"
    ? offlineExams
    : exams;

  // Stats by type for the current active pool
  const typeCounts = currentPool.reduce((acc, e) => {
    acc[e.exam_type] = (acc[e.exam_type] || 0) + 1;
    return acc;
  }, {});

  const filtered = currentPool.filter((e) => {
    const matchSearch = !search ||
      (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.class_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.subject_name || "").toLowerCase().includes(search.toLowerCase());
    const matchClass  = !classFilter || String(e.class_id) === String(classFilter);
    const matchBatch  = !batchFilter || String(e.batch_id) === String(batchFilter);
    const matchType   = typeFilter === "all" || e.exam_type === typeFilter;
    return matchSearch && matchClass && matchBatch && matchType;
  });

  const statItems = Object.entries(EXAM_TYPE_LABELS).filter(([k]) => typeCounts[k]);

  const classesForModal  = (teacherClasses  && teacherClasses.length  > 0) ? teacherClasses  : schoolClasses;
  const subjectsForModal = (teacherSubjects && teacherSubjects.length > 0) ? teacherSubjects : classSubjects;

  const upcomingCount = exams.filter((e) => e.exam_date && new Date(e.exam_date) >= new Date()).length;

  return (
    <div className="lms-content-inner" style={{ paddingTop: 0 }}>
      {/* Header card */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 14,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}>
        <div style={{ height: 3, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />
        <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <ClipboardList size={20} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827" }}>My Exams</div>
            <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 1 }}>
              Create and manage exams for your assigned classes
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ textAlign: "center", background: "#eff6ff", borderRadius: 10, padding: "5px 14px", border: "1.5px solid #bfdbfe" }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1d4ed8", lineHeight: 1.1 }}>{onlineExams.length}</div>
              <div style={{ fontSize: 9.5, color: "#2563eb", fontWeight: 800, letterSpacing: "0.05em", marginTop: 2 }}>ONLINE</div>
            </div>
            <div style={{ textAlign: "center", background: "#faf5ff", borderRadius: 10, padding: "5px 14px", border: "1.5px solid #e9d5ff" }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#7e22ce", lineHeight: 1.1 }}>{offlineExams.length}</div>
              <div style={{ fontSize: 9.5, color: "#9333ea", fontWeight: 800, letterSpacing: "0.05em", marginTop: 2 }}>OFFLINE</div>
            </div>
          </div>

          <button onClick={() => { setEditExam(null); setShowModal(true); }} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            borderRadius: 9, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <Plus size={15} /> Create Exam
          </button>
        </div>
      </div>

      {/* Main Mode Navigation Tabs (Online, Offline, All) */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "14px",
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        {/* Tab 1: All Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("all"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "all" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "all" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "all" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Layers size={16} color={mainTab === "all" ? "#1c498d" : "#94a3b8"} />
          <span>All Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "all" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "all" ? "#1e40af" : "#64748b",
            }}
          >
            {exams.length}
          </span>
        </button>

        {/* Tab 2: Online Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("online"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "online" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "online" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "online" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Globe size={16} color={mainTab === "online" ? "#1c498d" : "#94a3b8"} />
          <span>Online Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "online" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "online" ? "#1e40af" : "#64748b",
            }}
          >
            {onlineExams.length}
          </span>
        </button>

        {/* Tab 3: Offline Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("offline"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "offline" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "offline" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "offline" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <ClipboardList size={16} color={mainTab === "offline" ? "#1c498d" : "#94a3b8"} />
          <span>Offline Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "offline" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "offline" ? "#1e40af" : "#64748b",
            }}
          >
            {offlineExams.length}
          </span>
        </button>
      </div>

      {/* Search + Filters */}
      <div className="crs-filters" style={{ marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="crs-search-wrap" style={{ flex: 1, minWidth: 180 }}>
          <Search size={14} />
          <input placeholder="Search exams by name, class, subject..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="crs-filter-select" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="">All Classes</option>
          {(teacherClasses && teacherClasses.length > 0 ? teacherClasses : schoolClasses).map((c) => (
            <option key={c.class_id || c.id} value={c.class_id || c.id}>{c.class_name || c.name}</option>
          ))}
        </select>
        <select className="crs-filter-select" value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}
          disabled={!classFilter} style={{ opacity: classFilter ? 1 : 0.5 }}>
          <option value="">All Batches</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>{b.name || b.batch_name}</option>
          ))}
        </select>
        <select className="crs-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {EXAM_TYPES.map((t) => (
            <option key={t} value={t}>{EXAM_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <button onClick={loadExams} style={{
          padding: "7px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
          background: "#fff", color: "#6b7280", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5, fontSize: 13, flexShrink: 0,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Category Filter Chips */}
      {statItems.length > 0 && (
        <div style={{ display: "flex", gap: "7px", marginBottom: "14px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setTypeFilter("all")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "4px 12px",
              borderRadius: "18px",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer",
              border: typeFilter === "all" ? "1.5px solid #1c498d" : "1px solid #e2e8f0",
              background: typeFilter === "all" ? "#eff6ff" : "#ffffff",
              color: typeFilter === "all" ? "#1c498d" : "#64748b",
              transition: "all 0.15s ease",
            }}
          >
            <BarChart2 size={12} /> All ({currentPool.length})
          </button>
          {statItems.map(([key, label]) => {
            const tc = EXAM_TYPE_COLORS[key] || EXAM_TYPE_COLORS.unit_test;
            const active = typeFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTypeFilter(active ? "all" : key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: active ? `1.5px solid ${tc.color}` : "1px solid #e2e8f0",
                  background: active ? tc.bg : "#ffffff",
                  color: active ? tc.color : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                {label} ({typeCounts[key]})
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
        }}>
          <ClipboardList size={48} color="#e5e7eb" style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>No exams found</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            {search || typeFilter !== "all" || classFilter ? "Try adjusting your filters." : "Create your first exam to get started."}
          </div>
          <button onClick={() => { setEditExam(null); setShowModal(true); }} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Plus size={14} /> Create First Exam
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 20 }}>
          {filtered.map((exam) => (
            <ExamCard
              key={exam.id}
              exam={exam}
              onEdit={(e) => { setEditExam(e); setShowModal(true); }}
              onDelete={setConfirmDel}
              onView={setViewExam}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ExamModal
          exam={editExam}
          schoolId={schoolId}
          classes={classesForModal}
          classSubjects={subjectsForModal}
          onClose={() => { setShowModal(false); setEditExam(null); }}
          onSaved={loadExams}
        />
      )}
      {viewExam && (
        <ExamDetailDrawer
          exam={viewExam}
          schoolId={schoolId}
          onClose={() => setViewExam(null)}
          onNavigate={(path) => { setViewExam(null); nav(path.replace("/lms/", "/teacher-lms/")); }}
        />
      )}
      {confirmDel && (
        <div className="ms2-modal-overlay" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 14, padding: "32px 28px", width: 380,
            maxWidth: "92vw", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete Exam?</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              Delete <strong>{confirmDel.name}</strong>? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setConfirmDel(null)} className="crs-btn-cancel">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{
                padding: "9px 22px", borderRadius: 8, border: "none",
                background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                {deleting ? <Loader size={13} className="ms2-spin" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
